import Axios from 'axios';

import { HttpStatusCodeEnum, ZepQuizApiError } from '@/api/httpClient';

const isApiErrorInErrorCodes = (error: unknown, httpCodes: number[]) => {
  if (error instanceof ZepQuizApiError) {
    return httpCodes.includes(error.httpStatus);
  }

  if (Axios.isAxiosError(error)) {
    return httpCodes.includes(error.response?.status || 200);
  }
};
/** 전역 인증 실패 판별은 401 만 본다. */
export const isAuthenticationError = (error: unknown) =>
  isApiErrorInErrorCodes(error, [HttpStatusCodeEnum.UNAUTHORIZED_401]);

export const isRateLimitedError = (error: unknown) =>
  isApiErrorInErrorCodes(error, [HttpStatusCodeEnum.TOO_MANY_REQUESTS_429]) ===
  true;

/**
 * "현재 로그인한 사용자가 서버에 없다" 는 응답인지 본다.
 *
 * 로컬에서 dev-N 을 바꾸거나 백엔드 QA 데이터를 재시드하면 브라우저에 남은
 * `localStorage.token` 이 더 이상 존재하지 않는 quiz user 를 가리킬 수 있다.
 * 서버는 이 경우 401 이 아니라 "해당 유저를 찾을 수 없습니다" 로 응답한다.
 *
 * 이 판별은 **현재 사용자 조회(`/auth/profile`) 호출부에서만** 쓴다. 전역
 * 에러 핸들러에 물리면 남의 유저를 조회하는 일반 API 의 같은 404(예: 삭제된
 * 학생 상세)까지 걸려 선생님 세션이 날아간다. 세션 정리는 UserProvider 가
 * `fetchUserInfo()` 결과를 보고 직접 한다.
 */
export const isCurrentUserNotFoundError = (error: unknown) => {
  if (!(error instanceof ZepQuizApiError)) return false;

  return (
    error.message.includes('해당 유저를 찾을 수 없습니다') ||
    error.status === 'USER_NOT_FOUND'
  );
};

export const isCanceledError = (error: unknown) => Axios.isCancel(error);

// 취소 + 401/403 을 묶어 판별하는 공유 구현. 계약이 다른 소비자들이 각자
// 아래 목적별 래퍼를 쓰도록 해, 한 소비자의 요구로 이 판별을 바꿨다가 다른
// 소비자가 깨지는 사고를 막는다.
const isCanceledOrAuthError = (_error: unknown) => {
  if (Axios.isCancel(_error)) {
    return true;
  }

  return isApiErrorInErrorCodes(_error, [
    HttpStatusCodeEnum.UNAUTHORIZED_401,
    HttpStatusCodeEnum.FORBIDDEN_403,
  ]);
};

/**
 * 에러 바운더리에서 UI 를 띄우지 않고 조용히 넘길 에러인지 판별한다.
 * 취소/401/403 은 useApiError 전역 처리 경로가 담당하므로, 바운더리는
 * 폴백 UI 를 렌더하지 않고 리셋만 한다.
 *
 * "유저 없음" 은 여기 넣지 않는다. 남의 유저를 조회하는 일반 API(삭제된 학생의
 * getClassroomMemberStats 등)도 같은 404 를 내는데, 그걸 suppress 하면 정상적인
 * 404 UI 대신 빈 화면 + reset 반복이 된다. 현재 사용자 정리는 UserProvider 담당.
 */
export const isErrorBoundarySuppressedError = (_error: unknown) =>
  isCanceledOrAuthError(_error);

/**
 * 프로필 조회(useGetAuthProfile) 에서 바운더리로 던지지 않고 조용히 무시할
 * 에러인지 판별한다. 취소/401/403 은 비로그인·세션만료 등 정상 상태이므로
 * 에러로 승격하지 않는다.
 */
export const isAuthProfileIgnoredError = (_error: unknown) =>
  isCanceledOrAuthError(_error) || isCurrentUserNotFoundError(_error);

// 404 에러, 400 에러는 무시
export const isIgnoreRetryError = (_error: unknown) => {
  return isApiErrorInErrorCodes(_error, [
    HttpStatusCodeEnum.UNAUTHORIZED_401,
    HttpStatusCodeEnum.FORBIDDEN_403,
    HttpStatusCodeEnum.BAD_REQUEST_400,
    HttpStatusCodeEnum.NOT_FOUND_404,
    HttpStatusCodeEnum.CONFLICT_409,
    HttpStatusCodeEnum.TOO_MANY_REQUESTS_429,
    HttpStatusCodeEnum.INTERNAL_SERVER_ERROR_500,
  ]);
};

export const LONG_REQUEST_TIMEOUT = 5 * 60 * 1000;
